import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useFilaReembolsosPlataforma } from '../../../../hooks/useFilaReembolsosPlataforma';
import { PedidoReembolsoCard, ContadoresStatus } from '../../../../components/financeiro';
import {
  ALTERAR_STATUS_REEMBOLSO,
  FINALIZAR_REEMBOLSO,
  AUDITORIAS_PEDIDO_REEMBOLSO,
} from '../../../../services/pedidoReembolsoService';
import type { PedidoReembolsoAuditoria, StatusReembolso } from '../../../../types/pedidoReembolso';
import type { PeriodoFinanceiro } from '../../../../types/financeiro';

// Aba "Reembolsos" do hub financeiro da plataforma -- AC-6 (plano AC-A3). Recebe { periodo } por
// prop, chama useFilaReembolsosPlataforma(periodo). Renderiza ContadoresStatus (contagens
// prontas do servidor) + um PedidoReembolsoCard por pedido com isOrganizador={false}/
// isAdmin={true} -- "Executar Reembolso" nunca aparece (ação exclusiva do organizador, que
// detém o token Iugu da liga; PedidoReembolsoCard já esconde o botão quando isOrganizador é
// false). Diálogo de histórico inline (mesmo padrão de
// pages/liga/financeiro/ReembolsosLigaTab.tsx:57-189), com loading/error próprios e retry --
// nunca falha silenciosamente.

interface AuditoriasPedidoReembolsoData {
  auditoriasPedidoReembolso: PedidoReembolsoAuditoria[];
}

interface AuditoriasPedidoReembolsoVars {
  pedidoReembolsoId: string;
}

export interface ReembolsosTabProps {
  periodo: PeriodoFinanceiro;
}

const STATUS_OPTIONS: StatusReembolso[] = ['PENDENTE', 'APROVADO', 'RECUSADO', 'FINALIZADO'];

const ReembolsosTab: React.FC<ReembolsosTabProps> = ({ periodo }) => {
  const { pedidos, contagens, filtroStatus, setFiltroStatus, loading, error, refetch } =
    useFilaReembolsosPlataforma(periodo);

  const [alterarStatus] = useMutation(ALTERAR_STATUS_REEMBOLSO);
  const [finalizar] = useMutation(FINALIZAR_REEMBOLSO);
  const [buscarAuditorias, { data: auditoriaData, loading: historicoLoading, error: historicoError }] =
    useLazyQuery<AuditoriasPedidoReembolsoData, AuditoriasPedidoReembolsoVars>(AUDITORIAS_PEDIDO_REEMBOLSO);

  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historicoPedidoId, setHistoricoPedidoId] = useState<string | null>(null);

  const handleAprovar = async (pedidoId: string) => {
    await alterarStatus({ variables: { input: { pedidoId, novoStatus: 'APROVADO' } } });
    refetch();
  };

  const handleRecusar = async (pedidoId: string) => {
    await alterarStatus({ variables: { input: { pedidoId, novoStatus: 'RECUSADO' } } });
    refetch();
  };

  const handleFinalizar = async (pedidoId: string) => {
    await finalizar({ variables: { pedidoId } });
    refetch();
  };

  const handleHistorico = (pedidoId: string) => {
    setHistoricoPedidoId(pedidoId);
    buscarAuditorias({ variables: { pedidoReembolsoId: pedidoId } });
    setHistoricoAberto(true);
  };

  const handleRetryHistorico = () => {
    if (historicoPedidoId) {
      buscarAuditorias({ variables: { pedidoReembolsoId: historicoPedidoId } });
    }
  };

  if (error) {
    return (
      <Box data-testid="reembolsos-erro">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }
        >
          Erro ao carregar reembolsos. Tente novamente.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ContadoresStatus contagens={contagens} />

      <Box mb={2} maxWidth={220}>
        <Select
          size="small"
          fullWidth
          value={filtroStatus ?? ''}
          onChange={(e: SelectChangeEvent) => setFiltroStatus((e.target.value || null) as StatusReembolso | null)}
          displayEmpty
        >
          <MenuItem value="">
            <em>Todos os status</em>
          </MenuItem>
          {STATUS_OPTIONS.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {pedidos.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="reembolsos-vazio">
          Nenhum pedido de reembolso no período.
        </Typography>
      ) : (
        pedidos.map((pedido) => (
          <PedidoReembolsoCard
            key={pedido.id}
            pedido={pedido}
            isOrganizador={false}
            isAdmin
            canExecute={false}
            executeDisabledReason={null}
            onAprovar={() => handleAprovar(pedido.id)}
            onRecusar={() => handleRecusar(pedido.id)}
            onExecutar={() => {}}
            onFinalizar={() => handleFinalizar(pedido.id)}
            onHistorico={() => handleHistorico(pedido.id)}
          />
        ))
      )}

      <Dialog open={historicoAberto} onClose={() => setHistoricoAberto(false)}>
        <DialogTitle>Histórico do pedido</DialogTitle>
        <DialogContent>
          {historicoError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetryHistorico}>
                  Tentar novamente
                </Button>
              }
            >
              Erro ao carregar histórico. Tente novamente.
            </Alert>
          ) : historicoLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (auditoriaData?.auditoriasPedidoReembolso ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum registro de histórico para este pedido.
            </Typography>
          ) : (
            <List>
              {(auditoriaData?.auditoriasPedidoReembolso ?? []).map((item) => (
                <ListItem key={item.id}>
                  <ListItemText primary={item.acao} secondary={item.realizadoEm ?? undefined} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReembolsosTab;
